import { useCallback, useEffect, useState } from "react";
import useFetch from "hooks/useFetch";
import useSignalR from "hooks/useSignalR";
import { getFetchOptions } from "utils/fetch";

// REST snapshot + SignalR updates so the publish button stays gated across reloads / user switches. Idle when draftId is falsy.
export const useDraftProcessingStatus = (draftId) => {
	const [count, setCount] = useState(0);
	const { execute } = useFetch();
	const { connection, registerHandler, onConnected } = useSignalR();

	const refresh = useCallback(() => {
		if (!draftId) {
			return;
		}
		execute(`/api/v3/Course/Draft/${draftId}/processing-status`, getFetchOptions({ method: "GET" }), (data, error) => {
			if (!error && data) {
				setCount(data.count ?? data.Count ?? 0);
			}
		});
	}, [draftId, execute]);

	useEffect(() => {
		if (!draftId) {
			setCount(0);
			return;
		}
		refresh();
	}, [draftId, refresh]);

	useEffect(() => {
		if (!connection || !draftId) {
			return;
		}
		const groupName = `draft-processing-${draftId}`;

		onConnected(async (conn) => {
			await conn.invoke("JoinGroupAsync", groupName);
		});

		const handleReconnect = async () => {
			await connection.invoke("JoinGroupAsync", groupName);
			refresh();
		};
		connection.onreconnected(handleReconnect);

		const unregister = registerHandler("DraftProcessingChanged", groupName, (message) => {
			const payload = message?.Payload ?? message?.payload ?? {};
			setCount(payload.count ?? payload.Count ?? 0);
		});

		return () => {
			unregister();
			connection.invoke("LeaveGroupAsync", groupName).catch(() => { /* connection may already be down */ });
		};
	}, [connection, draftId, registerHandler, onConnected, refresh]);

	return {
		isServerProcessing: count > 0,
		serverProcessingCount: count,
		refreshProcessingStatus: refresh
	};
};
